import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { Button } from 'react-bootstrap';

import FlashRoot from 'components/FlashMessage/FlashRoot';
import {
  flashMessages,
  reviewSchoolYears as reviewSchoolYearsDuck,
  customers as customersDuck
} from 'ducks';
import SchoolYearSelect from '../../SchoolYearSelect';
import { duckify, singleDuck } from '../../../utils/duckify';
import ReviewMenu from './ReviewMenu';
import withSchoolYear from '../../../hocs/withSchoolYear';
import Placeholder from '../../Placeholder';
import ConfirmChangePageModal from './ConfirmChangePageModal';
import './styles/ReviewSettings.scss';

import DocumentsForReview from './DocumentsForReview';
import CandidateCoverSheet from './CandidateCoverSheet';
import RatingCritera from './RatingCritera';
import ColumnsAndFilters from './ColumnsAndFilters';
import DecisionTypes from './DecisionTypes';

const SetupReviewYear = ({
  schoolYear,
  onSubmit
}) => {
  const [configuring, setConfiguring] = useState(false);

  const handleClick = () => {
    setConfiguring(true);
    onSubmit(schoolYear.id)
      .then(() => {
        setConfiguring(false);
      });
  };

  return (
    <div>
      <p className='review-setup-warning'>
        Reviews have not yet been set up for&nbsp;
        {schoolYear.name}
        .&nbsp;Are you ready to set them up?
      </p>
      <p>
        This will copy forward your settings from the previous year. Then you can edit and update
        any settings you need to.
      </p>
      <Button
        disabled={configuring}
        onClick={handleClick}
      >
        {configuring ? 'Configuring...' : 'Setup Review'}
      </Button>
    </div>
  );
};

SetupReviewYear.propTypes = {
  /** Currently Selected School Year */
  schoolYear: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    review_enabled: PropTypes.bool
  }).isRequired,

  /** onSubmit function */
  onSubmit: PropTypes.func
};

SetupReviewYear.defaultProps = {
  onSubmit: () => false
};

const ReviewSettingsBody = (props) => {
  let pageBody;

  switch (props.params.page) {
    case 'documents':
      pageBody = (
        <DocumentsForReview {...props} />
      );
      break;
    case 'cover_sheet':
      pageBody = (
        <CandidateCoverSheet {...props} />
      );
      break;
    case 'rating_criteria':
      pageBody = (
        <RatingCritera {...props} />
      );
      break;
    case 'columns_and_filters':
      pageBody = (
        <ColumnsAndFilters {...props} />
      );
      break;
    case 'decision_types':
      pageBody = (
        <DecisionTypes {...props} />
      );
      break;
    default:
      pageBody = <Placeholder.loading />;
      break;
  }
  return pageBody;
};

const ReviewSettingsBodyWithRouter = withRouter(ReviewSettingsBody);

const ReviewSettings = (props) => {
  const {
    customer,
    reviewSettings,
    onSchoolYearChange,
    reviewSchoolYears,
    enableReviewYear,
    schoolYearId,
    allDucksReady,
    showFlash,
    reloadYears,
    location,
    router,
    route,
    params
  } = props;
  const [selectedYear, setSelectedYear] = useState({});
  const [validYears, setValidYears] = useState(false);
  const [showConfirmChangeYear, setShowConfirmChangeYear] = useState(null);

  useEffect(() => {
    setValidYears(false);
    setShowConfirmChangeYear(null);

    if (allDucksReady) {
      let currentSchoolYear;
      if (schoolYearId) {
        currentSchoolYear = reviewSchoolYears.find(year => year.id === schoolYearId);
      } else {
        currentSchoolYear = reviewSchoolYears.find(year => year.review_enabled);
      }

      // if review_enable is not not true for any school year
      if (!currentSchoolYear) {
        // eslint-disable-next-line prefer-destructuring
        currentSchoolYear = reviewSchoolYears[0];
      }

      // If currentSchoolYear. review_enabled is false, no valid review years were found.
      setValidYears(currentSchoolYear.review_enabled);
      setSelectedYear(currentSchoolYear);
      onSchoolYearChange(currentSchoolYear.id);
    }
  }, [allDucksReady, schoolYearId]);

  const onSetupReviewYear = (newSchoolYearId) => (
    enableReviewYear(newSchoolYearId)
      .then(() => reloadYears().then(() => onSchoolYearChange(newSchoolYearId)))
      .catch(() => (
        showFlash({
          type: 'danger',
          message: 'There was an issue loading this review year. If this persists please contact customer support.'
        })
      ))
  );

  const onSchoolYearSelect = (year) => {
    if (reviewSettings.dirty) {
      setShowConfirmChangeYear(year);
    } else {
      onSchoolYearChange(year);
    }
  };

  return (
    <div className='review-settings'>
      <FlashRoot />
      <div className='review-settings-header'>
        <h2 className='review-settings-title'>Review settings for</h2>
        <SchoolYearSelect
          onSelect={onSchoolYearSelect}
          schoolYears={reviewSchoolYears}
          schoolYearId={selectedYear.id}
        />
        <div>These settings apply to your admissions review and selection process</div>
      </div>
      <Placeholder.loading ready={allDucksReady}>
        {!selectedYear.review_enabled ? (
          <SetupReviewYear
            schoolYear={selectedYear}
            onSubmit={onSetupReviewYear}
          />
        ) : (
          <React.Fragment>
            <ConfirmChangePageModal
              show={showConfirmChangeYear !== null}
              handleClose={() => setShowConfirmChangeYear(null)}
              handleConfirm={() => onSchoolYearChange(showConfirmChangeYear)}
            />
            {validYears && (
              <div className='review-settings-body'>
                <ReviewMenu
                  className='review-settings-menu'
                  page={params.page}
                  schoolYearId={selectedYear.id}
                />
                {
                  <ReviewSettingsBodyWithRouter
                    className='review-settings-body-component'
                    customer={customer}
                    schoolYearId={selectedYear.id}
                    route={route}
                  />
                }
              </div>
            )}
          </React.Fragment>
        )}
      </Placeholder.loading>
    </div>
  );
};

ReviewSettings.propTypes = {
  /** Current customer data */
  customer: PropTypes.shape({}),

  /** Review School years configs */
  reviewSchoolYears: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Review Settings state */
  reviewSettings: PropTypes.shape({
    dirty: PropTypes.bool
  }),

  /** enableReviewYear, create call for reviewSchoolYears duck */
  enableReviewYear: PropTypes.func.isRequired,

  /** The selected school year */
  schoolYearId: PropTypes.string,

  /** allDucksReady bool */
  allDucksReady: PropTypes.bool,

  /** ShowFlash Duck */
  showFlash: PropTypes.func,

  /** Called when the school year selector is changed */
  onSchoolYearChange: PropTypes.func,

  /** React Router Route object */
  route: PropTypes.shape({}),

  /** React Router Params object */
  params: PropTypes.shape({
    page: PropTypes.string
  })
};

ReviewSettings.defaultProps = {
  customer: {},
  reviewSettings: { dirty: false },
  schoolYearId: '',
  allDucksReady: false,
  showFlash: () => false,
  route: {},
  params: {},

  onSchoolYearChange: () => false
};

const mapDucksToProps = () => ({
  reviewSchoolYears: reviewSchoolYearsDuck,
  customer: singleDuck(customersDuck, 'current')
});

const mapStateToProps = ({ reviewSettings }) => ({
  reviewSettings
});

const mapDispatchToProps = dispatch => ({
  enableReviewYear: schoolYearId => (
    dispatch(reviewSchoolYearsDuck.actions.create({ school_year_id: schoolYearId }))
  )
    .then(reviewSchoolYearsDuck.actions.denormalizeObjects),
  reloadYears: () => dispatch(reviewSchoolYearsDuck.actions.markDirty()),
  showFlash: (props) => dispatch(flashMessages.actions.show(props))
});

export default compose(
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps),
  withSchoolYear(() => ''),
  withRouter
)(ReviewSettings);
